import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, SubmissionError } from 'redux-form';
import PropTypes from 'prop-types';
import ButtonMenu from 'components/buttons/ButtonMenu';
import DOMPurify from 'dompurify';
import MenuItem from 'components/Menu/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import { get, isEmpty, startCase } from 'lodash';
import axios from 'axios';

import { getLetterInAlphabet, truncateText } from 'utils/misc';
import {
  requiredValidator,
  requiredQuillValidator,
  handleBlur,
} from 'utils/validators';
import RenderFormInput from 'components/forms/RenderFormInput';
import { secondary100, secondary25 } from 'themes/colors';
import TestEmailModal from 'components/modals/TestEmailModal/index';
import { Can, AbilityContext } from 'utils/permissions';
import expandIcon from 'icons/expandIcon.svg';
import minimizeIcon from 'icons/minimizeIcon.svg';
import messageIcon from 'icons/messageIcon.svg';
import deleteIcon from 'icons/deleteIcon.svg';
import classNames from 'classnames';
import { apiPitchUploadImage } from 'constants/apiRouting';
import { SUBJECT_INHERITED } from 'constants/pitch';
import { filterLegacyTemplateFields } from 'utils/steps';
import { Editor } from '@tinymce/tinymce-react';
import { getPitches, getPitchCompletion } from '../../../redux/actions/pitchActions';
import RemoveCustomizationModal from './RemoveCustomizationModal';
import { deleteCustomizedPitch, setIsContactCustomized } from '../../../redux/actions/contactActions';
import { accentThree800 } from '../../../themes/colors';

const styles = () => ({
  pitchTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1.25rem',
    border: `1px solid ${secondary100}`,
    borderRadius: '0.25rem',
    backgroundColor: secondary25,
    marginTop: '1.25rem',
    '&.error': {
      borderColor: accentThree800,
    },
  },
  pitchTitleLetter: {
    padding: '0.125rem 0.565rem',
    backgroundColor: secondary100,
    borderRadius: '0.25rem',
    marginRight: '0.625rem',
  },
  fieldTitleLetter: {
    backgroundColor: secondary100,
    borderRadius: '0.25rem',
    position: 'absolute',
    left: '1.25rem',
    top: '1.0rem',
    padding: '0.125rem 0.565rem',
  },
  pitchTitle: {
    fontWeight: '500',
    cursor: 'grab',
  },
  expendIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  minimizeIcon: {
    position: 'absolute',
    top: '0.875rem',
    right: '1.25rem',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  pitchContent: {
    display: 'inline-block',
    width: '100%',
  },
  pitchSubject: {
    position: 'relative',
    border: `1px solid ${secondary100}`,
    borderRadius: '0.25rem',
    marginTop: '1.5rem',

    '&>div': {
      height: 'auto !important',
    },
  },
  pitchSubjectInput: {
    fontWeight: '500',
    backgroundColor: `${secondary25} !important`,
    padding: '0.75rem 13.25rem 0.75rem 3.75rem !important',
  },
  toolbarBorder: {
    position: 'absolute',
    top: '5rem',
    width: '94%',
    borderBottom: '1px solid #eef1f5 !important',
  },
  pitchBody: {
    '& .ql-toolbar': {
      maxWidth: '25rem',
      borderBottom: 'none !important',
    },
    position: 'relative',
    border: `1px solid ${secondary100}`,
    borderTop: 'none',
    borderBottomLeftRadius: '0.25rem',
    borderBottomRightRadius: '0.25rem',
    padding: '1rem 1.5rem',
  },
  selectVariables: {
    position: 'absolute',
    right: '1.5rem',
    top: '1rem',
  },
  testEmailSection: {
    cursor: 'pointer',
    position: 'absolute',
    top: '0.875rem',
    display: 'flex',
    right: '3.5rem',
    '& > img': {
      marginRight: '0.5rem',
    },
  },
  removeCustomizationSection: {
    cursor: 'pointer',
    position: 'absolute',
    display: 'flex',
    top: '1.5rem',
    right: '12.375rem',
    color: '#76849C',

    '& > img': {
      marginRight: '0.5rem',
    },
  },
});

class PitchPreview extends Component {
  static propTypes = {
    classes: PropTypes.element.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    deleteCustomizedPitch: PropTypes.func.isRequired,
    pristine: PropTypes.bool,
    invalid: PropTypes.bool,
    submitting: PropTypes.bool,
    id: PropTypes.number,
    parentId: PropTypes.number,
    name: PropTypes.string,
    subject: PropTypes.string,
    body: PropTypes.string,
    currentStepIndex: PropTypes.number.isRequired,
    currentPitchIndex: PropTypes.number.isRequired,
    onExpandPitch: PropTypes.func.isRequired,
    onCancelPitch: PropTypes.func.isRequired,
    inheritsSubject: PropTypes.bool.isRequired,
    getPitches: PropTypes.func.isRequired,
    onDestroyPitch: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    csvTemplateFields: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    contactPitchPreviews: PropTypes.shape({
      pitch_variables: PropTypes.object,
    }).isRequired,
  };
  static defaultProps = {
    name: null,
    subject: null,
    body: null,
    id: null,
    parentId: null,
    pristine: true,
    invalid: true,
    submitting: false,
  };
  constructor(props) {
    super(props);
    this.state = {
      testEmailModalOpen: false,
      removeCustomizationModalOpen: false,
      uploading: false,
      expanded: true,
    };
    this.quillRef = null;
    this.reactQuillRef = null;
    this.editorRef = React.createRef();
  }

  onExpand = () => {
    const {
      onExpandPitch, currentStepIndex, currentPitchIndex,
    } = this.props;
    onExpandPitch(currentStepIndex, currentPitchIndex);
    this.setState({
      expanded: true,
    });
  }

  onMinimize = (onCancelPitch, currentStepIndex, currentPitchIndex) => {
    onCancelPitch(currentStepIndex, currentPitchIndex);
    this.setState({
      expanded: false,
    });
  }

  onRemoveCustomizedPitch = async () => {
    try {
      const {
        contactSelectedId, id, onContactSelected, isUnCustomizedPitches,
      } = this.props;
      await this.props.deleteCustomizedPitch(contactSelectedId, id);
      this.toggleRemoveCustomizationModal();
      onContactSelected();
      // eslint-disable-next-line no-unused-expressions
      !isUnCustomizedPitches && this.props.setIsContactCustomized(contactSelectedId, false);
    } catch (error) {
      if (error.errors) {
        throw new SubmissionError(error.errors);
      }
    }
  }

  setUploading = (value) => {
    this.setState({ uploading: value });
  };

  getVariable = fieldName =>
    // This method responsible for retrieving variable value of the pitch.
    get(this.props.contactPitchPreviews.pitch_variables, fieldName, '');

  insertText = async (variableText) => {
    const range = this.quillRef.getSelection();
    const position = range ? range.index : 0;
    this.quillRef.insertText(position, `${this.getVariable(variableText)}`);
  };

  toggleEmailModal = () => {
    const { testEmailModalOpen } = this.state;
    this.setState({
      testEmailModalOpen: !testEmailModalOpen,
    });
  };

  toggleRemoveCustomizationModal = () => {
    const { removeCustomizationModalOpen } = this.state;
    this.setState({
      removeCustomizationModalOpen: !removeCustomizationModalOpen,
    });
  }

  handleOnChangeImg = input => (allDeltas, editor) => {
    input.onChange(editor.getHTML());
    this.setState({ uploading: false });
  };

  handleOnChange = input => (value) => {
    input.onChange(value);
  };

  attachRef = (ref) => {
    this.reactQuillRef = ref;
    if (!ref || typeof this.reactQuillRef.getEditor !== 'function') return;
    this.quillRef = this.reactQuillRef.getEditor();
  };
  imageUploadHandler = async (blobInfo) => {
    const formData = new FormData();
    formData.append('file', blobInfo.blob(), blobInfo.filename());
    const { data } = await axios.post(apiPitchUploadImage(), formData, {
      withCredentials: true,
    });
    return data.file_url;
  };
  renderQuill = ({ input }) =>
    (this.context.can('edit', 'Step') ? (
      <Editor
        uploading={this.state.uploading}
        setUploading={this.setUploading}
        apiKey="ckhfzoj2pneepbp2uyfc2kn0xxk6u2uzfqvjr07d5evi6iad"
        onInit={(evt, editor) => {
          this.editorRef.current = editor;

          // Change the icon of "merge tags" to be "{}"
          const buttons = document.querySelectorAll('button[title="Insert merge tag"] .tox-icon');

          for (let i = 0; i < buttons.length; i += 1) {
              buttons[i].innerHTML = `
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <text x="6" y="15" font-family="Arial" font-size="20" font-weight="bold" fill="black">{}</text>
                  </svg>
              `;
          }
        }}
        // initialValue="<p>This is the initial content of the editor.</p>"
        {...input}
        onEditorChange={this.handleOnChange(input)}
        init={{
          height: 500,
          menubar: true,
          plugins: [
             'ai', 'mentions', 'autolink', 'charmap', 'emoticons', 'image', 'link', 'lists', 'searchreplace', 'wordcount', 'checklist', 'casechange', 'export', 'formatpainter', 'permanentpen', 'mergetags', 'powerpaste', 'tinymcespellchecker', 'autocorrect', 'a11ychecker', 'typography', 'inlinecss',
          ],
          toolbar: 'mergetags | aidialog aishortcuts | bold italic underline strikethrough | fontfamily fontsize | image link emoticons charmap | alignment formatting indentation | removeformat | undo redo',
          toolbar_mode: 'floating',
          toolbar_groups: {
            formatting: {
              icon: 'ordered-list',
              tooltip: 'Lists',
              items: 'checklist numlist bullist',
            },
            indentation: {
              icon: 'indent',
              tooltip: 'Indentation',
              items: 'indent outdent',
            },
            alignment: {
              icon: 'align-left',
              tooltip: 'Alignment',
              items: 'align lineheight',
            },
          },
          content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px } p { margin: 0; }',
          contextmenu: 'advtemplate',

          mergetags_list: filterLegacyTemplateFields(this.props.csvTemplateFields.results).sort((a, b) => a.name.localeCompare(b.name)).map(field => ({
            value: field.name,
            title: startCase(field.name),
          })),
          mergetags_prefix: '{{ ',
          mergetags_suffix: ' }}',
          ai_request: (request, respondWith) => {
            const completion = this.props.getPitchCompletion(this.props.id, { prompt: request.prompt });
            respondWith.string(() => completion);
          },
          images_upload_handler: this.imageUploadHandler,
        }}
      />
    ) : (
      <div
        className="p-3"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(input.value) }}
      />
    ));

  render() {
    const {
      // eslint-disable-next-line camelcase
      subject,
      currentPitchIndex,
      csvTemplateFields,
      id,
      currentStepIndex,
      classes,
      onCancelPitch,
      inheritsSubject,
      parentId,
      contactSelectedId,
      errors,
    } = this.props;

    const { testEmailModalOpen, removeCustomizationModalOpen, expanded } = this.state;
    const subjectToDisplay = inheritsSubject ? SUBJECT_INHERITED : truncateText(subject, 40);


    if (!isEmpty(errors)) {
      return (
        <div>
          <div className={classNames(classes.pitchTitleContainer, 'error')}>
            <div>
              <span className={classes.pitchTitleLetter}>
                {getLetterInAlphabet(currentPitchIndex).toUpperCase()}
              </span>
              <span>
                Could not parse template. Go to &quot;
                <a href={`/sequences/${this.props.sequence.id}/steps`}>steps</a>&quot; to correct.
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <TestEmailModal
          isOpen={testEmailModalOpen}
          pitchId={id}
          sequenceContactId={contactSelectedId}
          toggle={this.toggleEmailModal}
        />
        <RemoveCustomizationModal
          removeCustomizationModalOpen={removeCustomizationModalOpen}
          toggleRemoveCustomizationModal={this.toggleRemoveCustomizationModal}
          onRemoveCustomizedPitch={this.onRemoveCustomizedPitch}
        />
        {!expanded && (
          <div className={classes.pitchTitleContainer}>
            <div>
              <span className={classes.pitchTitleLetter}>
                {getLetterInAlphabet(currentPitchIndex).toUpperCase()}
              </span>
              <span className={classes.pitchTitle}>
                {subjectToDisplay}
              </span>
            </div>
            <img
              className={classes.expendIcon}
              role="presentation"
              src={expandIcon}
              alt="expand_icon"
              onClick={this.onExpand}
            />
          </div>
        )}
        {expanded &&
          <div className={classes.pitchContent}>
            <div className={classes.pitchSubject}>
              <Field
                className={classes.pitchSubjectInput}
                maxLength="255"
                name="subject"
                label="Subject"
                value={subjectToDisplay}
                noBorder
                props={{ multiline: true }}
                component={RenderFormInput}
                type="text"
                onBlur={handleBlur}
                disabled={!this.context.can('edit', 'Step') || inheritsSubject}
                validate={[requiredValidator]}
              />
              <img
                className={classes.minimizeIcon}
                role="presentation"
                src={minimizeIcon}
                alt="minimize_icon"
                onClick={() => this.onMinimize(onCancelPitch, currentStepIndex, currentPitchIndex)}
              />
              <span className={classes.fieldTitleLetter}>
                {getLetterInAlphabet(currentPitchIndex).toUpperCase()}
              </span>
              <div className={classes.testEmailSection} role="presentation" onClick={this.toggleEmailModal}>
                <img
                  src={messageIcon}
                  alt="message_icon"
                />
                <span>Send test email</span>
              </div>
            </div>
            <div className={classes.pitchBody}>
              <div className="body__field-container">
                <Field
                  name="body"
                  component={this.renderQuill}
                  validate={[requiredQuillValidator]}
                />
              </div>
              <Can I="edit" a="Step">
                <ButtonMenu
                  className={classes.selectVariables}
                  btnText="Select variables"
                >
                  {filterLegacyTemplateFields(csvTemplateFields.results).map(field => (
                    <MenuItem
                      key={`field-${field.id}`}
                      onClick={() => this.insertText(field.name)}
                    >
                      {field.name}
                    </MenuItem>
                  ))}
                </ButtonMenu>
              </Can>
              {parentId &&
              <div
                role="presentation"
                className={classes.removeCustomizationSection}
                onClick={this.toggleRemoveCustomizationModal}
              >
                <img
                  role="presentation"
                  src={deleteIcon}
                  alt="delete_icon"
                />
                <span>Remove customization</span>
              </div>}
              <div className={classes.toolbarBorder} />
            </div>
          </div>
        }
      </div>
    );
  }
}

PitchPreview.contextType = AbilityContext;

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      sequence: state.sequence,
      pitches: state.pitches,
      csvTemplateFields: state.csvTemplateFields,
      contactPitchPreviews: state.contactPitchPreviews,
    }),
    {
      getPitches, deleteCustomizedPitch, setIsContactCustomized, getPitchCompletion,
    },
  ),
)(PitchPreview);
